import { useAppFilters } from '@/hooks';
import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * A custom hook to manage the UI state of chart sections in the dashboard.
 *
 * It tracks:
 * - Which sections are expanded or collapsed.
 * - Whether charts in each section are displayed in split or combined mode.
 * - Modal visibility per section (e.g., for filters or settings).
 *
 * @param sectionKeys - Array of section identifiers (e.g., ['vibration', 'temperature']).
 * @param splitDefault - Optional initial state for split chart mode per section.
 *
 * @returns An object containing:
 * - expandedSections: Current open/closed state of each section.
 * - splitCharts: Whether each section is rendered in split-chart mode.
 * - showModal: Modal visibility state per section.
 * - toggleSection: Function to expand/collapse a section.
 * - toggleSplitCharts: Function to toggle between split and combined chart view.
 * - handleShowModal: Function to control modal visibility per section.
 * - setShowModal: Direct setter for showModal state (for advanced usage).
 */
export function useChartSections(sectionKeys: string[], splitDefault: Record<string, boolean>) {
  //TMP
  const { selectedComponent } = useAppFilters();

  // Track which sections are currently expanded (default: all true)
  const [expandedSections, setExpandedSections] = useState(() => {
    const init: Record<string, boolean> = {};
    sectionKeys.forEach((key) => {
      init[key] = true;
    });
    return init;
  });

  // Update expandedSections when sectionKeys change, preserving existing states
  // Only add new sections as expanded, keep existing sections' state unchanged
  useEffect(() => {
    setExpandedSections((prev) => {
      const updated: Record<string, boolean> = {};
      sectionKeys.forEach((key) => {
        // Preserve existing state or default to true for new sections
        updated[key] = prev[key] !== undefined ? prev[key] : true;
      });
      return updated;
    });
  }, [sectionKeys]);

  // Track whether charts in each section should be split by sensor (or shown as combined)
  const [splitCharts, setSplitCharts] = useState(() => {
    // If splitDefault has keys, use it; otherwise initialize all sections to false
    const hasKeys = splitDefault && Object.keys(splitDefault).length > 0;
    if (hasKeys) {
      return splitDefault;
    }
    // Initialize all sections to false
    return Object.fromEntries(sectionKeys.map((key) => [key, false]));
  });

  // Track modal visibility per section (e.g., filter modal, chart config modal)
  const [showModal, setShowModal] = useState(() => Object.fromEntries(sectionKeys.map((key) => [key, false])));

  // Toggle expanded/collapsed state of a section
  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Toggle between split and combined chart view for a section
  const toggleSplitCharts = useCallback((section: string) => {
    setSplitCharts((prev) => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Show or hide a modal for a specific section
  const handleShowModal = useCallback(
    (section: string, enabled: boolean) => {
      setShowModal((prev) => {
        const newState = { ...prev, [section]: enabled };
        return newState;
      });
    },
    [] // Remove showModal from dependencies to prevent re-creation
  );

  return {
    expandedSections,
    splitCharts,
    showModal,
    toggleSection,
    toggleSplitCharts,
    handleShowModal,
    setShowModal,
  };
}
